/**
 * Inquiry Routing Logic for Spire Group
 *
 * Business Rules (2026-05-28 — simplified to single first-responder + escalation):
 * - EVERY lead → Reign (+63 960 253 5917), the single first responder.
 * - If Reign doesn't reply within the window, /api/cron/lead-nudge nudges her, then
 *   escalates to Yogesh (+63 906 300 8674). Yogesh is the escalation backstop, NOT an assignee.
 * - Janssen REMOVED entirely (was ≥₱25M; slow/offline → leads going cold).
 */

export const INQUIRY_AGENTS = {
  REIGN: {
    name: 'Reign',
    phone: '+63 960 253 5917',
    email: 'spiregroup88@gmail.com',
  },
  // 2026-05-28: Janssen REMOVED from routing (slow/offline → leads lost). Yogesh is the
  // ESCALATION contact (pinged by the nudge cron when Reign hasn't replied), not an
  // auto-assignee. (Was Janssen +63 917 764 7619.)
  YOGESH: {
    name: 'Yogesh',
    phone: '+63 906 300 8674',
    email: 'yash@housal.com',
  },
} as const;

/**
 * EA roster — name → WhatsApp number. Single source of truth for both the inbox
 * "Assigned to" dropdown AND the first-message sign-off: a lead's reply is signed by
 * whoever it is assigned to (the first responder claims the lead, then messages).
 * ⚠️ CONFIRM with Yogesh: Janssen's number (from old routing) + Marj's number (unknown).
 */
export const EA_ROSTER: Array<{ name: string; phone: string }> = [
  { name: 'Reign', phone: '+63 960 253 5917' },
  { name: 'Janssen', phone: '+63 917 764 7619' }, // confirmed 2026-06-02
  { name: 'Marj',    phone: '+63 917 651 8989' }, // confirmed 2026-06-02
  { name: 'Yogesh',  phone: '+63 906 300 8674' }, // Yash — confirmed 2026-06-02
];

/** Resolve an EA's WhatsApp number from the roster (null if unknown / Unassigned). */
export function eaPhone(name: string | null | undefined): string | null {
  if (!name) return null;
  const hit = EA_ROSTER.find((e) => e.name.toLowerCase() === name.toLowerCase());
  return hit?.phone || null;
}

export const SALE_HIGH_THRESHOLD = 25_000_000; // ₱25M

/**
 * Determines the correct agent based on property listing type and price
 *
 * @param listingType - Property listing type (e.g., "For Rent", "For Sale")
 * @param price - Property price (numeric value, may be undefined)
 * @returns Agent object with name, phone, and email
 */
export function getInquiryAgent(_listingType?: string, _price?: number) {
  // 2026-05-28 flow change: ALL leads go to Reign first (single first-responder).
  // Yogesh is the ESCALATION backstop (see /api/cron/lead-nudge) — pinged only when Reign
  // hasn't replied within the window. Janssen removed entirely.
  return INQUIRY_AGENTS.REIGN;
}

/**
 * Parses price string/number and returns numeric value
 * Handles various formats: "₱25,000,000", "25000000", 25000000
 *
 * @param priceString - Price as string or number
 * @returns Numeric price value or undefined if invalid
 */
export function parsePrice(priceString?: string | number): number | undefined {
  if (typeof priceString === 'number') return priceString;
  if (!priceString) return undefined;

  // Remove ₱ symbol, commas, and convert to number
  const cleaned = priceString.toString().replace(/[₱,]/g, '');
  const parsed = parseFloat(cleaned);

  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Formats phone number for WhatsApp links (digits only)
 * Example: "+63 960 253 5917" → "639602535917"
 *
 * @param phone - Phone number with any formatting
 * @returns Phone number with only digits
 */
export function formatPhoneForWhatsApp(phone: string): string {
  return phone.replace(/\D/g, ''); // Remove all non-digits
}

/**
 * Formats phone number for tel: links (keep + and digits)
 * Example: "+63 960 253 5917" → "+639602535917"
 *
 * @param phone - Phone number with any formatting
 * @returns Phone number with + and digits only
 */
export function formatPhoneForTel(phone: string): string {
  return phone.replace(/[^\d+]/g, ''); // Keep + and digits only
}
